/**
 * Payment Status Enum
 * Must match PaymentStatusEnum in backend (src/Enum/PaymentStatusEnum.php)
 * 
 * Pricing tiers:
 * - 0-1000 lots: 89€/month, 490€ starter fees
 * - 1000-2000 lots: 149€/month, 980€ starter fees
 * - 2000+ lots: 249€/month, 1980€ starter fees
 */
export enum PaymentStatus {
  ACTIVE = 'active',
  // 0-1000 lots tier (89€/month, 490€ starter fees)
  ACTIVE_UNDER_1000 = 'active_under_1000',
  // 1000-2000 lots tier (149€/month, 980€ starter fees)
  ACTIVE_1000_TO_2000 = 'active_1k_to_2k',
  // 2000+ lots tier (249€/month, 1980€ starter fees)
  ACTIVE_OVER_2000 = 'active_over_2000',

  INACTIVE = 'inactive',
  // 0-1000 lots tier
  INACTIVE_UNDER_1000 = 'inactive_under_1000',
  INACTIVE_UNDER_1000_NO_FEE = 'inactive_u1k_nofee',
  INACTIVE_UNDER_1000_50_PROMO = 'inactive_u1k_50promo',
  // 1000-2000 lots tier
  INACTIVE_1000_TO_2000 = 'inactive_1k_to_2k',
  INACTIVE_1000_TO_2000_NO_FEE = 'inactive_1k2k_nofee',
  INACTIVE_1000_TO_2000_50_PROMO = 'inactive_1k2k_50promo',
  // 2000+ lots tier
  INACTIVE_OVER_2000 = 'inactive_over_2000',
  INACTIVE_OVER_2000_NO_FEE = 'inactive_o2k_nofee',
  INACTIVE_OVER_2000_50_PROMO = 'inactive_o2k_50promo',

  TRIAL = 'trial',
  SUSPENDED = 'suspended',
  CANCELLED = 'cancelled',
  UNLIMITED_FREE = 'unlimited_free',
}

/**
 * Get all payment status values
 */
export function getPaymentStatusValues(): PaymentStatus[] {
  return Object.values(PaymentStatus);
}

/**
 * Get label for a payment status
 */
export function getPaymentStatusLabel(status: PaymentStatus | string | null | undefined): string {
  if (!status) {
    return 'Unknown';
  }

  switch (status) {
    case PaymentStatus.ACTIVE:
      return 'Active';
    case PaymentStatus.ACTIVE_UNDER_1000:
      return 'Active (< 1000 lots)';
    case PaymentStatus.ACTIVE_1000_TO_2000:
      return 'Active (1000-2000 lots)';
    case PaymentStatus.ACTIVE_OVER_2000:
      return 'Active (> 2000 lots)';
    case PaymentStatus.INACTIVE:
      return 'Inactive';
    case PaymentStatus.INACTIVE_UNDER_1000:
      return 'Inactive (< 1000 lots)';
    case PaymentStatus.INACTIVE_UNDER_1000_NO_FEE:
      return 'Inactive (< 1000 lots, sans frais)';
    case PaymentStatus.INACTIVE_UNDER_1000_50_PROMO:
      return 'Inactive (< 1000 lots, -50% frais)';
    case PaymentStatus.INACTIVE_1000_TO_2000:
      return 'Inactive (1000-2000 lots)';
    case PaymentStatus.INACTIVE_1000_TO_2000_NO_FEE:
      return 'Inactive (1000-2000 lots, sans frais)';
    case PaymentStatus.INACTIVE_1000_TO_2000_50_PROMO:
      return 'Inactive (1000-2000 lots, -50% frais)';
    case PaymentStatus.INACTIVE_OVER_2000:
      return 'Inactive (> 2000 lots)';
    case PaymentStatus.INACTIVE_OVER_2000_NO_FEE:
      return 'Inactive (> 2000 lots, sans frais)';
    case PaymentStatus.INACTIVE_OVER_2000_50_PROMO:
      return 'Inactive (> 2000 lots, -50% frais)';
    case PaymentStatus.TRIAL:
      return 'Trial';
    case PaymentStatus.SUSPENDED:
      return 'Suspended';
    case PaymentStatus.CANCELLED:
      return 'Cancelled';
    case PaymentStatus.UNLIMITED_FREE:
      return 'Unlimited Free';
    default:
      return 'Unknown';
  }
}

/**
 * Check if a status represents active access
 */
export function isActivePaymentStatus(status: PaymentStatus | string | null | undefined): boolean {
  return status === PaymentStatus.ACTIVE 
    || status === PaymentStatus.ACTIVE_UNDER_1000 
    || status === PaymentStatus.ACTIVE_1000_TO_2000 
    || status === PaymentStatus.ACTIVE_OVER_2000 
    || status === PaymentStatus.UNLIMITED_FREE;
}

